import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Device, EstateDetailDoc, getEstate, Room, Service, statusBadge, statusLabel } from "./api";

/**
 * Estate detail — for a single-residence property OR a multi-unit property.
 * If `units` is non-empty, show the building-shared services first then a
 * section per unit. Otherwise just one flat list.
 */
export function EstateDetail() {
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<EstateDetailDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getEstate(id)
      .then((d) => setDoc(d))
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
    const t = setInterval(() => {
      if (id) getEstate(id).then(setDoc).catch(() => {});
    }, 10_000);
    return () => clearInterval(t);
  }, [id]);

  // Group services by unit when applicable.
  const groups = useMemo(() => {
    if (!doc) return { shared: [] as Service[], byUnit: {} as Record<string, Service[]> };
    const shared: Service[] = [];
    const byUnit: Record<string, Service[]> = {};
    for (const s of doc.services) {
      if (s.scope === "unit" && s.unit_id) {
        (byUnit[s.unit_id] ??= []).push(s);
      } else {
        shared.push(s);
      }
    }
    return { shared, byUnit };
  }, [doc]);

  if (loading) return <div className="empty"><span className="spinner" /> Loading…</div>;
  if (err) return <div className="empty" style={{ color: "var(--down)" }}>Couldn't load — {err}</div>;
  if (!doc) return <div className="empty">Not found.</div>;

  const p = doc.profile;

  return (
    <>
      <div className="breadcrumb">
        <Link to="/">Portfolio</Link> &nbsp;›&nbsp; {p.name || id}
      </div>
      <h1>{p.name || id}</h1>
      <p className="subtitle">
        {p.address || ""} {p.type && <span style={{ color: "var(--text-2)" }}>· {p.type.replace(/_/g, " ")}</span>}
      </p>

      {/* Building-shared services */}
      {groups.shared.length > 0 && (
        <>
          <h2>Shared infrastructure</h2>
          <ServiceTable services={groups.shared} />
        </>
      )}

      {/* Per-unit groups */}
      {doc.units.map((u) => {
        const list = groups.byUnit[u.id] || [];
        const unitRooms = (doc.rooms || []).filter((r) => r.unit_id === u.id && r.type === "Room");
        const unitDevices = (doc.devices || []).filter((d) => d.unit_id === u.id);
        if (list.length === 0 && unitRooms.length === 0 && unitDevices.length === 0) return null;
        return (
          <div key={u.id}>
            <h2>{u.name}</h2>
            {list.length > 0 && <ServiceTable services={list} />}
            {unitRooms.length > 0 && (
              <RoomDeviceTree rooms={unitRooms} devices={unitDevices} />
            )}
          </div>
        );
      })}
    </>
  );
}

/**
 * Expandable per-room device list. Each room collapses by default and
 * shows count + a chevron; clicking expands the device list with
 * KNX individual address + product. Pulled from the ETS .knxproj
 * file — 60 rooms / 291 devices total across Totteridge Manor.
 */
/** Tiny coloured dot showing whether a KNX device has transmitted on
 *  the bus recently. Many KNX devices only transmit when something
 *  happens (button push, sensor change) so "no recent telegram" isn't
 *  a hard "dead" signal — labels are intentionally neutral.
 *
 *   <1h    green   "1h"     — actively transmitting
 *   <24h   amber   "12h"
 *   <7d    slate   "3d"
 *   >7d    grey    "30d+"
 *   never  hollow  "—"      — passive device or never seen
 */
function DevicePresenceDot({ ts }: { ts?: number }) {
  const color = ts ? presenceColor(ts) : "transparent";
  const border = ts ? color : "var(--border)";
  return (
    <span style={{
      width: 8, height: 8, borderRadius: "50%",
      background: color, border: `1px solid ${border}`,
      flexShrink: 0,
    }} />
  );
}

function presenceColor(ts: number): string {
  const ageSec = (Date.now() - ts) / 1000;
  if (ageSec < 3600) return "#10b981";          // green — within 1h
  if (ageSec < 86400) return "#f59e0b";         // amber — within 24h
  if (ageSec < 86400 * 7) return "#64748b";     // slate — within a week
  return "#475569";                              // dark grey — older
}

function presenceLabel(ts?: number): string {
  if (!ts) return "never seen";
  const ageSec = Math.max(1, (Date.now() - ts) / 1000);
  if (ageSec < 60) return `${Math.round(ageSec)}s ago`;
  if (ageSec < 3600) return `${Math.round(ageSec / 60)}m ago`;
  if (ageSec < 86400) return `${Math.round(ageSec / 3600)}h ago`;
  if (ageSec < 86400 * 30) return `${Math.round(ageSec / 86400)}d ago`;
  return "30d+ ago";
}

function RoomDeviceTree({ rooms, devices }: { rooms: Room[]; devices: Device[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const byRoom = useMemo(() => {
    const m: Record<string, Device[]> = {};
    for (const d of devices) {
      if (d.room_id) (m[d.room_id] ??= []).push(d);
    }
    return m;
  }, [devices]);

  // Sort rooms alphabetically for predictability.
  const sorted = [...rooms].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="rooms-tree" style={{ marginTop: 12, marginBottom: 24 }}>
      <h3 style={{ fontSize: 13, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: 0.5, margin: "8px 0" }}>
        Rooms &amp; devices ({sorted.length} rooms · {devices.length} devices)
      </h3>
      {sorted.map((r) => {
        const list = byRoom[r.room_id] || [];
        const isOpen = !!open[r.room_id];
        return (
          <div key={r.room_id} style={{ borderBottom: "1px solid var(--border)" }}>
            <button
              type="button"
              onClick={() => setOpen((p) => ({ ...p, [r.room_id]: !isOpen }))}
              style={{
                width: "100%", textAlign: "left", padding: "10px 8px",
                background: "transparent", border: "none", color: "var(--text)",
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
              }}
            >
              <span style={{ width: 12, display: "inline-block", color: "var(--text-2)" }}>
                {isOpen ? "▾" : "▸"}
              </span>
              <span style={{ flex: 1 }}>{r.name}</span>
              <span style={{ color: "var(--text-2)", fontSize: 12 }}>
                {list.length} device{list.length === 1 ? "" : "s"}
              </span>
            </button>
            {isOpen && list.length > 0 && (
              <div style={{ padding: "4px 8px 12px 32px" }}>
                {list.map((d) => (
                  <div key={d.device_id} style={{ display: "flex", gap: 12, padding: "4px 0", fontSize: 13, alignItems: "center" }}>
                    <DevicePresenceDot ts={d.last_telegram_ts} />
                    <span style={{ color: "var(--text-2)", fontFamily: "monospace", minWidth: 60 }}>
                      {d.address}
                    </span>
                    <span style={{ flex: 1 }}>{d.name}</span>
                    <span style={{ color: "var(--text-2)", fontSize: 11, minWidth: 100, textAlign: "right" }}>
                      {presenceLabel(d.last_telegram_ts)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ServiceTable({ services }: { services: Service[] }) {
  if (services.length === 0) return null;
  return (
    <div className="service-table">
      {services.map((s) => {
        const badge = statusBadge(s.latest_status);
        return (
          <div key={s.service_id} className="service-row">
            <div>
              <div className="name">{s.name || s.service_id}</div>
              <div className="cat">{s.category || ""}</div>
            </div>
            <div className="ip">{s.ip || "—"}</div>
            <div className="latency">
              {typeof s.latest_latency_ms === "number" ? `${s.latest_latency_ms.toFixed(1)} ms` : "—"}
            </div>
            <div>
              <span className={`status-tag ${badge}`}>
                <span className={`dot ${badge}`} /> {statusLabel(s.latest_status)}
              </span>
            </div>
            <div style={{ color: "var(--text-2)", fontSize: 12 }}>
              {s.latest_ts ? `${Math.max(1, Math.floor((Date.now() - s.latest_ts) / 1000))}s ago` : "no data"}
            </div>
          </div>
        );
      })}
    </div>
  );
}
